import React, { useState, useEffect, useRef } from 'react'; 
import {
  Modal, Box, Typography, Stepper, Step, StepLabel,
  Button, Paper, TextField, CircularProgress, Snackbar, Alert,
  Autocomplete, Tooltip
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

const steps = ['Subir XML', 'Completa información', 'Confirmación'];

const UploadModal = ({ open, onClose, refreshTable }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [cfdiData, setCfdiData] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [ordenCompra, setOrdenCompra] = useState('');
  const [entradaMercancia, setEntradaMercancia] = useState('');
  const [documentoFinanciero, setDocumentoFinanciero] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pdfValidating, setPdfValidating] = useState(false);
  const [entradasOptions, setEntradasOptions] = useState([]);
  const [searchingEntradas, setSearchingEntradas] = useState(false);
  const [entradasError, setEntradasError] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [mostrarErrorEntrada, setMostrarErrorEntrada] = useState(false);
  const [sapResult, setSapResult] = useState(null);

  // === PRIMER ERROR PEGADO + control de snackbar ===
  const firstErrorRef = useRef(null);      // guarda el PRIMER error mientras el modal esté abierto
  const [snackOpen, setSnackOpen] = useState(false); // controla la visibilidad del Snackbar

  // ===== Flags derivados =====
  const esPago = cfdiData?.tipoComprobante === 'P';
  const ocInicia6000 = (ordenCompra || '').startsWith('6000');
  // Entrada requerida solo si NO es 'P' y la OC NO inicia con 6000
  const entradaRequerida = !esPago && !ocInicia6000;

  // Cuando se vuelve opcional, limpiamos errores visuales
  useEffect(() => {
    if (!entradaRequerida) {
      setMostrarErrorEntrada(false);
      setEntradasError(''); // que no pinte rojo el campo si es opcional
    }
  }, [entradaRequerida]);

  // --------- VALIDACIONES ----------
  const isFormValid = () => {
    if (pdfValidating) return false;
    if (!pdfFile) return false;
    if (esPago) {
      return documentoFinanciero.length === 10;
    }
    if (!ordenCompra || ordenCompra.length !== 10) return false;
    if (entradaRequerida) {
      return !!entradaMercancia;
    }
    return true;
  };

  const getValidationMessage = () => {
    if (pdfValidating) return 'Validando PDF de la factura';
    if (!pdfFile) return 'Se requiere adjuntar el PDF de la factura';
    if (esPago) {
      if (!documentoFinanciero) return 'Ingrese el documento financiero';
      if (documentoFinanciero.length !== 10) return 'El documento financiero debe tener 10 dígitos';
      return '';
    }
    if (!ordenCompra) return 'Ingrese la orden de compra';
    if (ordenCompra.length !== 10) return 'La orden de compra debe tener 10 dígitos';
    if (entradaRequerida && !entradaMercancia) return 'Seleccione una entrada de mercancía';
    return '';
  };

  // ------ Helpers de error / axios ------

  // Agrega la leyenda solo cuando el mensaje contenga SINGLE_ERROR
  const appendImportantNoteIfSingleError = (msg) => {
    if (!msg) return '';
    if (msg.includes('SINGLE_ERROR')) {
      const note =
        'Importante: Este tipo de error al cargar facturas se deben a la construcción del XML y deben corregirse con tu PAC o sistema de facturación. ' +
        'No es un error de Verde Valle. Solicita a tu proveedor eliminar prefijos namespaces no usados, declarar addendas en cfdi:Addenda, ' +
        'evitar schemaLocation redundantes y mantener sólo los namespaces SAT requeridos.';
      // Evitar duplicar la nota si ya fue agregada
      if (msg.includes('Importante: Este tipo de error al cargar facturas')) {
        return msg;
      }
      return `${msg}\n\n${note}`;
    }
    return msg;
  };

  const reportError = (msg) => {
    const normalizedMsg = appendImportantNoteIfSingleError(msg);
    if (!firstErrorRef.current) firstErrorRef.current = normalizedMsg; // Fija el PRIMER error
    setError(firstErrorRef.current);                                   // Mantén el primero en estado visible
    setSuccess(false);
    setSnackOpen(true);                                                // Abre el Snackbar (se cerrará a los 6s)
  };

  const handleSnackClose = (_e, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false); // oculta el snackbar, pero NO borra el primer error pegado
  };

  const getAxiosError = (err, fallback) =>
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback ||
    'Ocurrió un error inesperado';

  const handleOrdenCompraChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10); // Solo números y máximo 10
    setOrdenCompra(value);
    // Si cambia la OC, limpiamos selección/errores de entrada
    setEntradaMercancia('');
    setMostrarErrorEntrada(false);
    setEntradasError('');
    setEntradasOptions([]);
  };

  const buscarEntradasMercancia = async () => {
    if (!ordenCompra || !cfdiData?.rfcEmisor) {
      setEntradasError('Se requiere orden de compra y RFC del emisor');
      return;
    }
    if (ordenCompra.length !== 10) {
      setEntradasError('La orden de compra debe tener 10 dígitos');
      return;
    }

    setSearchingEntradas(true);
    setEntradasError('');
    setEntradasOptions([]);
    setEntradaMercancia('');

    try {
      const response = await axios.get('https://facturas.verdevalle.com.mx/api/sap/pedidos', {
        withCredentials: true,
        params: { documento: ordenCompra, proveedor: cfdiData.rfcEmisor }
      });

      const rows = Array.isArray(response.data) ? response.data : [];
      if (rows.length === 0) {
        if (entradaRequerida) setEntradasError('No se encontraron entradas para esta orden de compra');
        else setEntradasError('');
        return;
      }

      const opciones = rows.map(item => ({
        label: `${item.Entrada} - ${item.Desmaterial} - ${item.Cantidad} (${item.Importe} ${item.Moneda})`,
        value: item.Entrada,
        material: item.Material,
        cantidad: item.Cantidad
      }));

      setEntradasOptions(opciones);
      if (opciones.length === 1) {
        setEntradaMercancia(opciones[0].value);
        setMostrarErrorEntrada(false);
      }
    } catch (err) {
      console.error('Error al buscar entradas:', err);
      const errorMsg = getAxiosError(err, 'Error al consultar entradas de mercancía');
      setEntradasError(errorMsg);
    } finally {
      setSearchingEntradas(false);
    }
  };

  // --------- LIFECYCLE ----------
  useEffect(() => {
    if (!open) {
      // Reset completo al cerrar el modal: también limpiamos el primer error pegado
      setActiveStep(0);
      setCfdiData(null);
      setPdfFile(null);
      setPdfValidating(false);
      setOrdenCompra('');
      setEntradaMercancia('');
      setDocumentoFinanciero('');
      setError('');
      setLoading(false);
      setSuccess(false);
      setEntradasOptions([]);
      setEntradasError('');
      setValidationResult(null);
      setMostrarErrorEntrada(false);
      setSapResult(null);
      firstErrorRef.current = null; // <- IMPORTANTE
      setSnackOpen(false);
    }
  }, [open]);

  // --------- HANDLERS ----------
  const handleXmlUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('xmlFile', file);
      const response = await axios.post('https://facturas.verdevalle.com.mx/api/process-xml', formData);

      if (response.data.success) {
        const data = response.data.cfdi;
        setCfdiData(data);
        setOrdenCompra(data.ordenCompra || '');
        setEntradaMercancia(data.entradaMercancia || '');
        setDocumentoFinanciero(data.documentoFinanciero || '');
        setActiveStep(1);
      } else {
        reportError(response.data.error || 'Error al procesar el XML');
      }
    } catch (err) {
      const msg = getAxiosError(err, 'Error al comunicarse con el servidor');
      const details = err.response?.data?.details ? `: ${err.response.data.details}` : '';
      reportError(`${msg}${details}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setPdfFile(null);
    firstErrorRef.current = null;
    setError('');
    setSnackOpen(false);

    if (!cfdiData?.uuid) {
      reportError('Primero sube el XML para validar el PDF contra la factura');
      event.target.value = '';
      return;
    }

    setPdfValidating(true);

    try {
      const formData = new FormData();
      formData.append('pdfFile', file);
      formData.append('uuid', cfdiData.uuid);

      await axios.post('https://facturas.verdevalle.com.mx/api/validate-pdf-uuid', formData);
      setPdfFile(file);
      firstErrorRef.current = null;
      setError('');
      setSnackOpen(false);
    } catch (err) {
      const apiError = err.response?.data?.validation || err.response?.data;
      reportError(apiError?.error || apiError?.message || 'El PDF adjunto no coincide con la factura');
      event.target.value = '';
    } finally {
      setPdfValidating(false);
    }
  };

  const handleCompleteRegistration = async () => {
    const validationMsg = getValidationMessage();
    if (validationMsg) {
      reportError(validationMsg);
      if (validationMsg.includes('entrada de mercancía')) setMostrarErrorEntrada(true);
      return;
    }

    setLoading(true);
    setMostrarErrorEntrada(false);

    try {
      const formData = new FormData();

      let finalEntrada = null;
      let finalOC = null;
      let finalDocFin = null;

      if (esPago) {
        finalDocFin = documentoFinanciero;
      } else {
        finalOC = ordenCompra || null;
        finalEntrada = entradaRequerida ? (entradaMercancia || null) : (entradaMercancia || null);
      }

      const finalCfdi = {
        ...cfdiData,
        ordenCompra: finalOC,
        entradaMercancia: finalEntrada,
        documentoFinanciero: finalDocFin
      };

      formData.append('cfdiData', JSON.stringify(finalCfdi));
      formData.append('pdfFile', pdfFile);

      const response = await axios.post('https://facturas.verdevalle.com.mx/api/save-cfdi', formData);

      if (response.data.success) {
        setValidationResult(response.data.validation);
        setSapResult(response.data.sapResult);
        setSuccess(true);
        setActiveStep(2);
      } else {
        const errorDetails =
          response.data.validation?.errors?.join('\n') ||
          response.data.validation?.message ||
          'Error en validación';
        reportError(`Validación fallida: ${errorDetails}`);
      }
    } catch (err) {
      const apiError = err.response?.data?.validation || err.response?.data;
      if (apiError?.status === 'PDF_UUID_MISMATCH' || /PDF adjunto/i.test(apiError?.message || apiError?.error || '')) {
        reportError('El PDF adjunto no coincide con la factura');
        return;
      }
      const errorMsg = apiError?.message || apiError?.error || 'Error al guardar el CFDI';
      const errorDetails = apiError?.errors?.join('\n') || apiError?.details || err.message;
      reportError(`${errorMsg}: ${errorDetails}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (refreshTable) refreshTable();
    onClose();
  };

  // Para mostrar el * solo cuando es obligatoria
  const entradaLabel = `Entrada de mercancía${entradaRequerida ? ' *' : ''}`;
  const puedeGuardar = isFormValid();

  return (
    <Modal open={open} onClose={loading ? null : handleReset}>
      <Paper sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        maxWidth: '95%',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <Typography variant="h6" gutterBottom>Registrar nuevo documento</Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <Typography gutterBottom sx={{ mb: 2 }}>
              Selecciona el archivo XML del documento CFDI
            </Typography>
            <input
              accept=".xml"
              id="xml-upload"
              type="file"
              hidden
              onChange={handleXmlUpload}
              disabled={loading}
            />
            <label htmlFor="xml-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                fullWidth
                disabled={loading}
                sx={{ height: 100 }}
              >
                {loading ? 'Procesando XML...' : 'Seleccionar archivo XML'}
              </Button>
            </label>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        )}

        {activeStep === 1 && cfdiData && (
          <Box>
            <Typography variant="h6" gutterBottom>Completa la información</Typography>

            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Adjuntar PDF del documento <span style={{ color: 'red' }}>*</span>
              </Typography>
              <input
                accept=".pdf"
                id="pdf-upload"
                type="file"
                hidden
                onChange={handlePdfUpload}
                disabled={loading || pdfValidating}
              />
              <label htmlFor="pdf-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PictureAsPdfIcon />}
                  fullWidth
                  disabled={loading || pdfValidating}
                  sx={{
                    mb: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    paddingRight: 1
                  }}
                >
                  {pdfValidating ? 'Validando PDF...' : (pdfFile ? pdfFile.name : 'Seleccionar PDF')}
                </Button>
              </label>
            </Box>

            {esPago ? (
              <TextField
                label="Documento financiero *"
                variant="outlined"
                fullWidth
                value={documentoFinanciero}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setDocumentoFinanciero(value);
                }}
                sx={{ mb: 2 }}
                error={documentoFinanciero.length !== 10}
                helperText={documentoFinanciero.length !== 10 ?
                  'El documento financiero debe tener exactamente 10 dígitos numéricos' : ''}
                required
              />
            ) : (
              <>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  position: 'relative'
                }}>
                  <TextField
                    label="Orden de compra *"
                    variant="outlined"
                    fullWidth
                    value={ordenCompra}
                    onChange={handleOrdenCompraChange}
                    disabled={loading}
                    required
                    error={ordenCompra.length !== 10 && ordenCompra.length > 0}
                    helperText={ordenCompra.length !== 10 && ordenCompra.length > 0 ?
                      'La orden de compra debe tener exactamente 10 dígitos numéricos' : ''}
                    inputProps={{
                      maxLength: 10,
                      inputMode: 'numeric'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { paddingRight: '48px' }
                    }}
                  />
                  <Tooltip
                    title={!ordenCompra || !cfdiData?.rfcEmisor ?
                      "Ingrese la orden de compra para buscar" :
                      "Buscar entradas de mercancía"}
                    placement="top"
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={buscarEntradasMercancia}
                        disabled={!ordenCompra || !cfdiData?.rfcEmisor || searchingEntradas || ordenCompra.length !== 10}
                        color="primary"
                        sx={{
                          position: 'absolute',
                          right: '8px',
                          minWidth: '40px',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          padding: 0,
                          backgroundColor: searchingEntradas ? 'action.disabled' : 'primary.main',
                          '&:hover': {
                            backgroundColor: searchingEntradas ? 'action.disabled' : 'primary.dark',
                            transform: 'scale(1.1)'
                          },
                          boxShadow: 2,
                          transition: 'all 0.2s ease',
                          border: '2px solid white'
                        }}
                      >
                        {searchingEntradas ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SearchIcon sx={{ fontSize: 20 }} />
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                <Autocomplete
                  options={entradasOptions}
                  getOptionLabel={(option) => option?.label || ''}
                  value={entradasOptions.find(opt => opt.value === entradaMercancia) || null}
                  onChange={(_, newValue) => {
                    setEntradaMercancia(newValue?.value || '');
                    setMostrarErrorEntrada(entradaRequerida ? !newValue : false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={entradaLabel}
                      variant="outlined"
                      fullWidth
                      helperText={
                        entradaRequerida
                          ? (mostrarErrorEntrada ? 'Debe seleccionar una entrada de mercancía' : (entradasError || "Selecciona una entrada de la lista"))
                          : (entradasError || "Selecciona una entrada (opcional si la OC inicia con 6000)")
                      }
                      error={entradaRequerida ? (mostrarErrorEntrada || !!entradasError) : false}
                    />
                  )}
                  disabled={entradasOptions.length === 0 || searchingEntradas}
                  loading={searchingEntradas}
                  sx={{
                    mb: 2,
                    '& .MuiAutocomplete-popper': { zIndex: 1302 }
                  }}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  noOptionsText="No se encontraron entradas"
                  openOnFocus
                  autoHighlight
                  clearOnBlur={false}
                  blurOnSelect={false}
                  PaperComponent={({ children }) => (
                    <Paper sx={{
                      maxHeight: '60vh',
                      overflow: 'auto',
                      boxShadow: 3,
                      '& .MuiAutocomplete-option': {
                        minHeight: 'auto',
                        padding: '8px 16px',
                        whiteSpace: 'normal'
                      }
                    }}>
                      {children}
                    </Paper>
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      <div style={{ width: '100%' }}>
                        <Typography variant="body1">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Material: {option.material} | Cantidad: {option.cantidad}
                        </Typography>
                      </div>
                    </li>
                  )}
                />
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={() => setActiveStep(0)} disabled={loading} variant="outlined">
                Regresar
              </Button>
              <Button
                variant="contained"
                onClick={handleCompleteRegistration}
                disabled={!puedeGuardar || loading}
              >
                {loading ? 'Validando y guardando...' : 'Validar y Guardar'}
                {loading && <CircularProgress size={24} sx={{ ml: 1 }} />}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 2 && validationResult && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {esPago ? 'Comprobante de pago' : 'Factura'} registrado y validado exitosamente
            </Typography>

            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'left' }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Resultado de validación:</strong> {validationResult.status}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {validationResult.message}
              </Typography>

              {validationResult.uuid && (
                <Typography variant="body2"><strong>UUID:</strong> {validationResult.uuid}</Typography>
              )}

              {esPago && sapResult && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Procesamiento SAP:</strong> {sapResult.success ? 'Éxito' : 'Error'}
                </Typography>
              )}

              {validationResult.details && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Detalles:</strong> {validationResult.details}
                </Typography>
              )}

              {validationResult.efosStatus && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Estatus EFOS:</strong> {validationResult.efosStatus}
                </Typography>
              )}

              {validationResult.lista69 !== undefined && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Lista 69:</strong> {validationResult.lista69 ? 'Sí' : 'No'}
                </Typography>
              )}
            </Box>

            <Button variant="contained" onClick={handleReset} sx={{ mt: 3 }}>
              Finalizar
            </Button>
          </Box>
        )}

        {/* Snackbar controlado por booleano independiente */}
        <Snackbar
          open={snackOpen}
          autoHideDuration={6000}
          onClose={handleSnackClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="error"
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
            onClose={handleSnackClose}
          >
            {firstErrorRef.current || error}
          </Alert>
        </Snackbar>
      </Paper>
    </Modal>
  );
};

export default UploadModal;
